/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import { createUserWithEmailAndPassword, updateProfile, signInWithPopup } from "firebase/auth";
import { auth, googleProvider, db } from "../../../firebaseConfig.js"; // Ensure 'db' is imported for Firestore
import { getDoc, setDoc, doc, collection, addDoc, updateDoc, arrayUnion } from "firebase/firestore"; // Firestore methods
import { getFunctions, httpsCallable } from "firebase/functions"; // Import Firebase Functions
import "./SignIn.css";
import CustomizedSnackbars from "../MaterialUI/CustomizedSnackbars";
import { useNavigate, useLocation } from "react-router-dom"; // Import useNavigate and useLocation
import Button from "@mui/material/Button";
import { serverTimestamp } from "firebase/firestore";
import { defaultAgents, defaultDocs } from "../../constants/defaults";
import { sendWelcomeEmail } from "../../services/emailService";

const SignUp = ({ onSignUpSuccess, onSignUpError, handleClose, isModal, inviteEmail, inviteOrgId }) => {
  const navigate = useNavigate(); // Initialize useNavigate
  const location = useLocation(); // Initialize useLocation

  // Determine effective invite values from props or URL
  const queryParams = new URLSearchParams(location.search);
  const urlOrgId = queryParams.get("orgId");
  const urlEmail = queryParams.get("email");
  const effectiveInviteOrgId = inviteOrgId || urlOrgId;
  const effectiveInviteEmail = inviteEmail || urlEmail;

  const [firstName, setFirstName] = useState(""); // New state for firstName
  const [lastName, setLastName] = useState(""); // New state for lastName
  const [email, setEmail] = useState(effectiveInviteEmail || "");
  const [password, setPassword] = useState("");
  const [organizationInput, setOrganizationInput] = useState(effectiveInviteOrgId || "");
  const [orgChoice, setOrgChoice] = useState(effectiveInviteOrgId ? "join" : "create"); // 'create' or 'join'
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  useEffect(() => {
    if (effectiveInviteOrgId) {
      setOrgChoice("join");
      setOrganizationInput(effectiveInviteOrgId);
    }

    if (effectiveInviteEmail) setEmail(effectiveInviteEmail);
  }, [effectiveInviteOrgId, effectiveInviteEmail]);
  
  const functions = getFunctions(); // Initialize Firebase Functions

  const handleRedirect = async (uid) => {
    const redirectParams = new URLSearchParams(location.search);
    const redirectPath = redirectParams.get("redirect");

    if (redirectPath && redirectPath.startsWith("/views/")) {
      const viewId = redirectPath.split("/")[2];
      if (viewId) {
        try {
          console.log(`Calling addCollaboratorOnSignUp function for view: ${viewId}`);
          const addCollaboratorOnSignUp = httpsCallable(functions, "addCollaboratorOnSignUp");
          await addCollaboratorOnSignUp({ viewId: viewId });
          console.log(`Successfully triggered addCollaboratorOnSignUp for user ${uid} and view ${viewId}`);
        } catch (error) {
          console.error("Error calling addCollaboratorOnSignUp function:", error);
          // Optionally, handle this error in the UI. The user will still be redirected.
        }
      }
    }

    if (onSignUpSuccess) {
      onSignUpSuccess();
    } else if (redirectPath) {
      navigate(redirectPath);
    } else {
      navigate("/welcome"); // Redirect to onboarding for new users
      // navigate("/"); // Default redirect if no path is specified
    }
  };

  // Function to handle sign up with email and password
  const signUp = (e) => {
    e.preventDefault();
    if (!firstName || !lastName || !email || !password || !organizationInput) {
      setSnackbarMessage("Please fill out all fields.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (password.length < 6) {
      setSnackbarMessage("Password must be at least 6 characters long.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    createUserWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        const user = userCredential.user;

        return updateProfile(user, {
          displayName: `${firstName} ${lastName}`,
        })
          .then(() => {
            // Organization logic
            if (orgChoice === 'create') {
              // Create a new organization
              return addDoc(collection(db, "organizations"), {
                name: organizationInput,
                members: [user.uid],
                createdAt: serverTimestamp(),
              }).then(orgDocRef => {
                // Now set the user document with the organization ID
                return setDoc(doc(db, "users", user.uid), {
                  uid: user.uid,
                  email,
                  firstName,
                  lastName,
                  organizationId: orgDocRef.id, // Add organization ID to user
                  createdAt: serverTimestamp(),
                  agents: defaultAgents,
                  docs: defaultDocs,
                });
              });
            } else { // orgChoice === 'join'
              // Handle joining an existing organization
              const orgId = organizationInput.trim();
              if (!orgId) {
                return Promise.reject(new Error("Organization ID is required."));
              }
              const orgRef = doc(db, "organizations", orgId);
              return getDoc(orgRef).then(orgSnap => {
                if (orgSnap.exists()) {
                  // Organization exists, add user to it
                  return updateDoc(orgRef, {
                    members: arrayUnion(user.uid)
                  }).then(() => {
                    // Now set the user document with the organization ID
                    return setDoc(doc(db, "users", user.uid), {
                      uid: user.uid,
                      email,
                      firstName,
                      lastName,
                      organizationId: orgId,
                      createdAt: serverTimestamp(),
                      agents: defaultAgents,
                      docs: defaultDocs,
                    });
                  });
                } else {
                  // Organization does not exist
                  return Promise.reject(new Error("Organization not found."));
                }
              });
            }
          })
          .then(async () => {
            sendWelcomeEmail(email, firstName).then((result) => {
              if (!result.success) {
                console.warn("Welcome email failed to send:", result.error);
              }
            });

            setSnackbarMessage("Account created successfully!");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            
            await handleRedirect(user.uid);
          });
      })
      .catch((error) => {
        if (onSignUpError) {
          onSignUpError(error.message);
        }
        setSnackbarMessage(error.message);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  // Function to handle sign up with Google
  const signUpWithGoogle = () => {
    signInWithPopup(auth, googleProvider)
      .then((result) => {
        const user = result.user;
        const firstName = user.displayName.split(" ")[0] || "";
        const lastName = user.displayName.split(" ").slice(1).join(" ") || "";
        const userDocRef = doc(db, "users", user.uid);

        return getDoc(userDocRef).then(async (docSnap) => {
          const isNewUser = !docSnap.exists();

          if (isNewUser) {
            if (orgChoice === 'create') {
              // Create a new organization for new Google sign-up user
              return addDoc(collection(db, "organizations"), {
                name: organizationInput,
                members: [user.uid],
                createdAt: serverTimestamp(),
              }).then(orgDocRef => {
                const userData = {
                  uid: user.uid,
                  email: user.email,
                  firstName: firstName,
                  lastName: lastName,
                  organizationId: orgDocRef.id,
                  createdAt: serverTimestamp(),
                  agents: defaultAgents,
                  docs: defaultDocs,
                };
                return setDoc(userDocRef, userData, { merge: true });
              });
            } else { // orgChoice === 'join'
                const orgId = organizationInput.trim();
                if (!orgId) {
                    return Promise.reject(new Error("Organization ID is required."));
                }
                const orgRef = doc(db, "organizations", orgId);
                return getDoc(orgRef).then(orgSnap => {
                    if (orgSnap.exists()) {
                        return updateDoc(orgRef, {
                            members: arrayUnion(user.uid)
                        }).then(() => {
                            const userData = {
                                uid: user.uid,
                                email: user.email,
                                firstName: firstName,
                                lastName: lastName,
                                organizationId: orgId,
                                createdAt: serverTimestamp(),
                                agents: defaultAgents,
                                docs: defaultDocs,
                            };
                            return setDoc(userDocRef, userData, { merge: true });
                        });
                    } else {
                        return Promise.reject(new Error("Organization not found."));
                    }
                });
            }
          } else {
            // Existing user
            
            // If the user is joining an organization via invite, ensure they are added to members
            if (orgChoice === "join" && organizationInput) {
              const orgId = organizationInput.trim();
              const orgRef = doc(db, "organizations", orgId);
              const orgSnap = await getDoc(orgRef);
              
              if (orgSnap.exists()) {
                await updateDoc(orgRef, {
                  members: arrayUnion(user.uid)
                });
              }
            }

            const userData = {
              uid: user.uid,
              email: user.email,
              firstName: firstName,
              lastName: lastName,
            };
            return setDoc(userDocRef, userData, { merge: true });
          }
        }).then(async () => {
          // This .then() will execute for both new and existing users.
          // We can check if the user was new and send a welcome email.
          const docSnap = await getDoc(userDocRef);
          if (!docSnap.data()?.createdAt) { // A rough way to check if user was just created
            sendWelcomeEmail(user.email, firstName).then((result) => {
              if (!result.success) {
                console.warn("Welcome email failed to send:", result.error);
              }
            });
          }

          setSnackbarMessage("Signed up successfully with Google!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
            
          await handleRedirect(user.uid);
        });
      })
      .catch((error) => {
        if (onSignUpError) {
          onSignUpError(error.message);
        }
        console.log("Google Sign Up Error:", error.message);
        setSnackbarMessage(error.message);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleLoginClick = () => {
    // Navigate to login (root) but preserve the query parameters (orgId, redirect, etc.)
    navigate(`/${location.search}`);
  };

  return (
    <div className={`sign-in-container ${isModal ? "" : "full-page"}`}>
      {isModal && (
        <button onClick={handleClose} className="close-modal-button">
          X
        </button>
      )}
      <form onSubmit={signUp}>
        <h4 className="title">Create Account</h4>
        <input
          className="field"
          type="text"
          placeholder="Enter your first name"
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
          }}
        ></input>
        <input
          className="field"
          type="text"
          placeholder="Enter your last name"
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
          }}
        ></input>

        <div className="org-choice-container">
          <label>
            <input
              type="radio"
              name="orgChoice"
              value="create"
              checked={orgChoice === "create"}
              onChange={() => setOrgChoice("create")}
              disabled={!!effectiveInviteOrgId}
              title={effectiveInviteOrgId ? "Locked for invited user" : ""}
            />
            Create Organization
          </label>
          <label>
            <input
              type="radio"
              name="orgChoice"
              value="join"
              checked={orgChoice === "join"}
              onChange={() => setOrgChoice("join")}
              disabled={!!effectiveInviteOrgId}
              title={effectiveInviteOrgId ? "Locked for invited user" : ""}
            />
            Join Organization
          </label>
        </div>

        <input
          className="field"
          type="text"
          placeholder={orgChoice === 'create' ? "Enter your organization's name" : "Enter your organization's ID"}
          value={organizationInput}
          onChange={(e) => {
            setOrganizationInput(e.target.value);
          }}
          disabled={!!effectiveInviteOrgId}
          title={effectiveInviteOrgId ? "Organization ID is locked for this invite" : ""}
        ></input>
        
        <input
          className="field"
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          disabled={!!effectiveInviteEmail}
          title={effectiveInviteEmail ? "Email is locked for this invite" : ""}
        ></input>
        <input
          className="field"
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
          }}
        ></input>
        <button className="button" type="submit">
          Sign up
        </button>
        <button className="button" onClick={signUpWithGoogle} style={{ background: "var(--Grey1)", color: "var(--Grey7)" }}>
          Sign up with Google
          <svg xmlns="http://www.w3.org/2000/svg" height="40" width="40" viewBox="0 0 512 512" style={{ marginLeft: "12px" }}>
            <rect width="512" height="512" fill="#fff" rx="15%" />
            <path fill="#4285f4" d="M386 400c45-42 65-112 53-179H260v74h102c-4 24-18 44-38 57z" />
            <path fill="#34a853" d="M90 341a192 192 0 0 0 296 59l-62-48c-53 35-141 22-171-60z" />
            <path fill="#fbbc02" d="M153 292c-8-25-8-48 0-73l-63-49c-23 46-30 111 0 171z" />
            <path fill="#ea4335" d="M153 219c22-69 116-109 179-50l55-54c-78-75-230-72-297 55z" />
          </svg>
        </button>
      </form>
      
      {!isModal && (
        <div style={{ marginTop: "16px", textAlign: "center" }}>
          <span style={{ color: "var(--Grey7)", marginRight: "8px" }}>Already have an account?</span>
          <Button onClick={handleLoginClick} sx={{ textTransform: "none", padding: 0, minWidth: "auto", color: "var(--Blue)" }}>Log In</Button>
        </div>
      )}

      {!isModal && <Button onClick={handleBackClick}>Back</Button>}
      <CustomizedSnackbars open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} handleClose={() => setSnackbarOpen(false)} />
    </div>
  );
};

export default SignUp;
